// The card reveal, for Silver and Gold (and repeats of the gems, and a first on
// the site of the lower tiers): a centred card over a dimmed page, in the site's
// hand-made style. No sealed state: the card pops in, the badge assembles on a
// tier-coloured panel, then the words. A first on the site gets the
// "First on n450s" stamp after the name, and its line leads with it.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import BadgeArt from "../art/BadgeArt";
import type { UnrevealedCopy } from "../types";
import { tierStyle } from "../art";
import { useReducedMotion } from "./hooks";
import { useModalBehaviour } from "./overlay";
import RevealBurst from "./RevealBurst";
import type { RevealActions } from "./BadgeCinematic";
import { pointsLabel, repeatLabel, revealLine } from "./revealQueue";
import { tierLineText } from "../collection/badgeFormat";
import { TierLineView } from "../TierName";
import { FirstStamp } from "./FirstEver";
import { valueOf } from "../value";
import { STAMP_IMPACT_MS, cardTimeline, hexAlpha, particleCount, stageLight } from "./timeline";

export interface BadgeRevealCardProps extends RevealActions {
  copy: UnrevealedCopy;
  waiting?: number;
  /** Fading out after Collect. */
  closing?: boolean;
}

const BadgeRevealCard: React.FC<BadgeRevealCardProps> = ({ copy, waiting = 0, closing = false, onCollect, onShare, onSeeCollection, skipTier }) => {
  const reduced = useReducedMotion();
  const [artOn, setArtOn] = useState(false);
  const [burstOn, setBurstOn] = useState(false);
  const [shown, setShown] = useState(0);
  const [stampOn, setStampOn] = useState(false);
  const [impact, setImpact] = useState(false);
  const [shareState, setShareState] = useState<"idle" | "busy" | "failed">("idle");
  const rootRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const line = revealLine(copy);
  const first = !!copy.firstEver;
  // the tier's colour is the rarity: it lights the burst and the highlights
  const color = stageLight(copy.tier);
  const tier = tierStyle(copy.tier);
  const ordinal = copy.repeatIndex > 1 ? copy.repeatIndex : copy.count > 1 ? copy.count : 1;
  const repeat = repeatLabel(ordinal);
  // this copy's worth: a repeat is worth 4.5% less than the copy before it
  const value = valueOf(copy);

  useEffect(() => {
    const tl = cardTimeline(reduced, first);
    const ts = [
      window.setTimeout(() => setArtOn(true), tl.art),
      window.setTimeout(() => setBurstOn(true), tl.burst),
      window.setTimeout(() => setShown((s) => Math.max(s, 1)), tl.name),
      window.setTimeout(() => setShown((s) => Math.max(s, 2)), tl.details),
      window.setTimeout(() => setShown(3), tl.actions),
    ];
    if (tl.stamp !== null) {
      ts.push(window.setTimeout(() => setStampOn(true), tl.stamp));
      if (!reduced) ts.push(window.setTimeout(() => setImpact(true), tl.stamp + STAMP_IMPACT_MS));
    }
    cardRef.current?.focus({ preventScroll: true });
    return () => ts.forEach((t) => window.clearTimeout(t));
  }, [reduced, first]);

  // skipping ahead shows everything, the stamp included
  const showAll = useCallback(() => {
    setShown(3);
    if (first) setStampOn(true);
  }, [first]);

  const onEscape = useCallback(() => {
    if (shown < 3) showAll();
    else onCollect();
  }, [shown, showAll, onCollect]);
  useModalBehaviour(rootRef, onEscape);

  const share = async () => {
    if (shareState === "busy") return;
    setShareState("busy");
    try {
      await onShare();
      setShareState("idle");
    } catch {
      setShareState("failed");
    }
  };

  const vars = useMemo(
    () =>
      ({
        "--rarity": color,
        "--rarity-a": hexAlpha(color, 0.5),
        "--tier-fill": tier.fill,
        "--tier-gloss": tier.gloss,
        "--light": stageLight(copy.tier),
      }) as React.CSSProperties,
    [color, tier, copy.tier]
  );
  const burstColors = useMemo(() => [color, tier.fill, "#ffffff"], [color, tier.fill]);

  const node = (
    <div ref={rootRef} className={`bdg-cardrev${reduced ? " bdg-cardrev--reduced" : ""}${closing ? " is-closing" : ""}`} data-testid="badge-card-reveal" style={vars}>
      {/* a tap on the dimmed page collects, once the buttons are there */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="bdg-cardrev__scrim" onClick={() => (shown >= 3 ? onCollect() : showAll())} />
      <div
        ref={cardRef}
        className="bdg-cardrev__card"
        role="dialog"
        aria-modal="true"
        aria-label={`New badge: ${copy.name}`}
        tabIndex={-1}
      >
        <div className={`bdg-cardrev__stage${impact ? " is-stamped" : ""}`}>
          {waiting > 0 && <span className="bdg-cardrev__waiting">{waiting} more</span>}
          <div className="bdg-cardrev__rays" />
          {burstOn && !reduced && (
            <RevealBurst seed={copy.copyId} count={particleCount("card", copy.tier, first)} spread={120} colors={burstColors} ringColor="#000000" />
          )}
          {stampOn && <FirstStamp tier={copy.tier} reduced={reduced} className="bdg-cardrev__stamp" />}
          <div className="bdg-cardrev__art">
            {artOn && (
              <BadgeArt
                category={copy.category}
                tier={copy.tier}
                number={copy.number}
                code={copy.code}
                name={copy.name}
                count={ordinal > 1 ? ordinal : undefined}
                size={168}
                variant="full"
                animate="reveal"
              />
            )}
          </div>
        </div>
        <div className="bdg-cardrev__body">
          <p className={`bdg-cardrev__line${shown >= 1 ? " is-in" : ""}`} data-testid="badge-rarity-line">
            <TierLineView line={line} />
          </p>
          <h2 className={`bdg-cardrev__name${shown >= 1 ? " is-in" : ""}`}>
            {copy.name}
            {repeat && <span className="bdg-cardrev__repeat">{repeat}</span>}
          </h2>
          <div className={`bdg-cardrev__details${shown >= 2 ? " is-in" : ""}`}>
            {copy.description && <p className="bdg-cardrev__desc">{copy.description}</p>}
            {value > 0 && <p className="bdg-cardrev__points">{pointsLabel(value)}</p>}
          </div>
          <div className={`bdg-cardrev__actions${shown >= 3 ? " is-in" : ""}`}>
            <button type="button" className="bdg-btn bdg-btn--primary" onClick={onCollect} disabled={shown < 3}>
              Collect
            </button>
            <div className="bdg-cardrev__row">
              <button type="button" className="bdg-btn" onClick={share} disabled={shown < 3 || shareState === "busy"}>
                {shareState === "busy" ? "Making a link" : "Share"}
              </button>
              <button type="button" className="bdg-btn" onClick={onSeeCollection} disabled={shown < 3}>
                See collection
              </button>
            </div>
            {skipTier && (
              <button type="button" className="bdg-skip-tier" onClick={skipTier.onSkip} disabled={shown < 3}>
                {skipTier.label}
              </button>
            )}
            {shareState === "failed" && <p className="bdg-cardrev__error">Could not make a share link. Try again.</p>}
          </div>
        </div>
      </div>
      <p className="bdg-sr" aria-live="polite">
        {shown >= 1 ? `${copy.name}. ${tierLineText(line)}` : ""}
      </p>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(node, document.body) : node;
};

export default BadgeRevealCard;
